import { Link } from "react-router-dom";
import { Users, Maximize2, BedDouble, ArrowRight } from "lucide-react";
import { formatCurrency, formatArea } from "@/lib/utils/formatters";
import type { RoomType } from "@/types/rooms";
import { cn } from "@/lib/utils/cn";

interface RoomCardProps {
  room: RoomType;
  className?: string;
}

export default function RoomCard({ room, className }: RoomCardProps) {
  const cover = room.images?.[0];

  return (
    <article
      className={cn(
        "group overflow-hidden rounded-2xl bg-white shadow-card transition hover:shadow-modal",
        className,
      )}
    >
      <Link to={`/rooms/${room.slug}`} className="block">
        <div className="relative h-64 overflow-hidden bg-metro-cream-100">
          {cover ? (
            <img
              src={cover}
              alt={room.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <BedDouble size={40} className="text-gray-300" />
            </div>
          )}
          <div className="absolute bottom-4 right-4 rounded-full bg-white/95 px-3 py-1.5 shadow-sm backdrop-blur-sm">
            <span className="font-serif text-sm font-medium text-metro-green-950">
              {formatCurrency(room.basePrice)}
            </span>
            <span className="text-xs text-metro-ink-subtle"> / night</span>
          </div>
        </div>
      </Link>

      <div className="p-6">
        <Link to={`/rooms/${room.slug}`}>
          <h3 className="mb-1 font-serif text-xl text-metro-green-950 transition group-hover:text-metro-green-700">
            {room.name}
          </h3>
        </Link>
        <p className="mb-4 text-sm leading-relaxed text-metro-ink-muted line-clamp-2">
          {room.shortDescription}
        </p>
        <div className="mb-5 flex flex-wrap gap-4 border-t border-metro-cream-100 pt-4 text-xs text-metro-ink-muted">
          <span className="flex items-center gap-1.5">
            <Maximize2 size={13} className="text-metro-green-800" />
            {formatArea(room.size)}
          </span>
          <span className="flex items-center gap-1.5">
            <Users size={13} className="text-metro-green-800" />
            Up to {room.maxGuests} guests
          </span>
          <span className="flex items-center gap-1.5">
            <BedDouble size={13} className="text-metro-green-800" />
            {room.bedConfig}
          </span>
        </div>
        <Link
          to={`/rooms/${room.slug}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-metro-green-800 transition hover:gap-2.5"
        >
          View Room <ArrowRight size={15} />
        </Link>
      </div>
    </article>
  );
}